import { dateInRange } from 'shared';

import { Column, FilterState, Row, SortState } from './types';

export function defaultAccessor<T extends Row>(row: T, key: keyof T | string): string {
  const value = row[key as keyof T];

  if (typeof value === 'string') {
    return value;
  } else if (typeof value === 'number') {
    return value.toString();
  } else if (value === null) {
    return '';
  } else {
    try {
      return (value as unknown as any).toString();
    } catch {
      return '';
    }
  }
}

export function filterRows<T extends Row>({
  filterState,
  rows,
  columns
}: {
  filterState: FilterState[];
  rows: T[];
  columns: Column<T>[];
}) {
  if (filterState.length === 0) {
    return rows;
  }

  const filters = filterState.map(filter => ({ ...filter, substring: filter.substring?.toLowerCase() }));

  return rows.filter(row => {
    return filters.every(({ key, substring, oneOf, startDate, endDate, minValue, maxValue }) => {
      const rawValue = row[key as keyof T];
      const column = columns.find(c => c.key === key);

      const value = column?.filterValue
        ? column?.filterValue(row)
        : typeof rawValue === 'number'
          ? rawValue.toString()
          : rawValue;

      const fn = column?.filters?.textSearch?.fn;

      if (fn && substring) {
        return fn(row, substring);
      }

      if (substring) {
        if (typeof value !== 'string') return false;

        return value.toLowerCase().includes(substring);
      } else if (oneOf) {
        if (Array.isArray(value)) {
          return value.some(v => oneOf.includes(v));
        }

        // @ts-ignore
        return oneOf.includes(value);
      } else if (startDate || endDate) {
        if (typeof value !== 'string') return false;

        return dateInRange(new Date(value), startDate, endDate);
      } else if (minValue !== undefined || maxValue !== undefined) {
        if (minValue !== undefined && maxValue !== undefined) {
          return Number(value) >= Number(minValue) && Number(value) <= Number(maxValue);
        } else {
          return minValue !== undefined
            ? Number(value) >= Number(minValue)
            : true && maxValue !== undefined
              ? Number(value) <= Number(maxValue)
              : true;
        }
      }

      return false;
    });
  });
}

export function sortRows<T extends Row>({
  sortState,
  rows,
  columns,
  pinRowsToBottom
}: {
  sortState: SortState[];
  rows: T[];
  columns: Column<T>[];
  /** When set, rows matching this predicate are moved after all other rows (stable), after normal sort. */
  pinRowsToBottom?: (row: T) => boolean;
}) {
  const sorted =
    sortState.length === 0
      ? rows
      : [...rows].sort((row1, row2) => {
          let sortResult = 0;

          for (let index = 0; index < sortState.length; index++) {
            const { key, direction } = sortState[index];
            const column = columns.find(c => c.key === key);
            const sortBy = column?.sortBy;
            if (!sortBy) {
              continue;
            }

            const directionSign = direction === 'desc' ? -1 : 1;
            sortResult = sortBy(row1, row2) * directionSign;

            if (sortResult !== 0) {
              break;
            }
          }

          return sortResult;
        });

  if (!pinRowsToBottom) {
    return sorted;
  }

  const unpinned: T[] = [];
  const pinned: T[] = [];
  for (const row of sorted) {
    if (pinRowsToBottom(row)) {
      pinned.push(row);
    } else {
      unpinned.push(row);
    }
  }

  return [...unpinned, ...pinned];
}

/**
 * Determines if a column should be treated as a money/number column based on its key name
 * @param columnKey - The column key to check
 * @returns True if the column should be treated as a money/number column
 */
export function isMoneyColumn(columnKey: string | number): boolean {
  const key = columnKey.toString().toLowerCase();

  return (
    key.includes('amount') ||
    key.includes('price') ||
    key.includes('cost') ||
    key.includes('rent') ||
    key.includes('fee') ||
    key.includes('revenue') ||
    key.includes('payout') ||
    key.includes('budget') ||
    key.includes('deposit') ||
    key.includes('tcv') ||
    key.includes('mrr') ||
    key.includes('cents')
  );
}
